<template>
  <div class="thumbnail-selector">
    <!-- 動画プレーヤー（manifestUrlがある場合のみ表示） -->
    <div v-if="manifestUrl" class="thumbnail-selector__player">
      <videojs ref="playerRef" :src="manifestUrl" :poster="defaultThumbnailUrl" compact></videojs>
    </div>

    <!-- 時間入力 + フレーム抽出ボタン -->
    <div v-if="manifestUrl" class="thumbnail-selector__controls">
      <div class="thumbnail-selector__time-input">
        <label>{{ labels.timeLabel }}</label>
        <div class="d-flex flex-wrap align-items-center" style="gap: 8px;">
          <div class="input-group" style="width: 100px;">
            <input type="number" class="form-control" v-model="timeHours" min="0" placeholder="0">
            <div class="input-group-append">
              <span class="input-group-text">h</span>
            </div>
          </div>
          <div class="input-group" style="width: 100px;">
            <input type="number" class="form-control" v-model="timeMinutes" min="0" max="59" placeholder="00">
            <div class="input-group-append">
              <span class="input-group-text">m</span>
            </div>
          </div>
          <div class="input-group" style="width: 100px;">
            <input type="number" class="form-control" v-model="timeSeconds" min="0" max="59" step="1" placeholder="00">
            <div class="input-group-append">
              <span class="input-group-text">s</span>
            </div>
          </div>
          <div class="input-group" style="width: 115px;">
            <input type="number" class="form-control" v-model="timeMillis" min="0" max="999" step="1" placeholder="000">
            <div class="input-group-append">
              <span class="input-group-text">ms</span>
            </div>
          </div>
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="syncTimeFromPlayer"
            title="プレーヤーの現在時刻を取得"
          >
            <i class="fas fa-crosshairs"></i>
          </button>
        </div>
      </div>
      <button
        type="button"
        class="btn btn-primary mt-2"
        @click="extractFrame"
        :disabled="isExtracting"
      >
        <i class="fas fa-camera me-1"></i>
        <span v-if="isExtracting">{{ labels.extracting }}</span>
        <span v-else>{{ labels.extractButton }}</span>
      </button>
      <div v-if="timeError" class="text-danger mt-1" style="font-size: 0.875rem;">{{ timeError }}</div>
    </div>

    <!-- 画像アップロード -->
    <div class="thumbnail-selector__upload mt-3">
      <label class="btn btn-outline-primary">
        <i class="fas fa-upload me-1"></i>{{ labels.uploadCustom }}
        <input
          type="file"
          accept="image/*"
          style="display: none;"
          @change="uploadCustomImage"
          :disabled="isUploading"
        >
      </label>
      <span v-if="isUploading" class="ms-2 text-muted">{{ labels.uploading }}</span>
    </div>

    <!-- 候補グリッド -->
    <div class="thumbnail-selector__grid mt-3">
      <h6>{{ labels.candidates }}</h6>
      <div class="row g-2">
        <!-- デフォルトサムネイル -->
        <div class="col-4 col-md-3 col-lg-2">
          <div
            class="thumbnail-selector__item"
            :class="{ 'thumbnail-selector__item--selected': selectedId === null }"
            @click="resetToDefault"
          >
            <img :src="defaultThumbnailUrl" alt="default" class="img-fluid">
            <div class="thumbnail-selector__label">{{ labels.defaultLabel }}</div>
            <div v-if="selectedId === null" class="thumbnail-selector__badge">
              <i class="fas fa-check"></i>
            </div>
          </div>
        </div>

        <!-- DB候補一覧 -->
        <div v-for="c in readyCandidates" :key="c.id" class="col-4 col-md-3 col-lg-2">
          <div
            class="thumbnail-selector__item"
            :class="{ 'thumbnail-selector__item--selected': selectedId === c.id }"
            @click="selectCandidate(c.id)"
          >
            <img :src="c.cdn_url" :alt="c.thumbnail_key" class="img-fluid">
            <div class="thumbnail-selector__label">
              <template v-if="c.source === 'frame'">{{ formatTimestamp(c.timestamp_seconds) }}</template>
              <template v-else>{{ labels.uploadSource }}</template>
            </div>
            <div v-if="selectedId === c.id" class="thumbnail-selector__badge">
              <i class="fas fa-check"></i>
            </div>
            <button
              type="button"
              class="thumbnail-selector__delete"
              @click.stop="deleteCandidate(c.id)"
              title="削除"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Pending候補（生成中） -->
        <div v-for="c in pendingCandidates" :key="'pending-' + c.id" class="col-4 col-md-3 col-lg-2">
          <div class="thumbnail-selector__item thumbnail-selector__item--pending">
            <div class="thumbnail-selector__placeholder">
              <i class="fas fa-spinner fa-spin"></i>
            </div>
            <div class="thumbnail-selector__label">{{ labels.extracting }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  videoId: { type: [String, Number], required: true },
  manifestUrl: { type: String, default: null },
  defaultThumbnailUrl: { type: String, default: null },
  selectedThumbnailId: { type: Number, default: null },
  uploadDuration: { type: Number, default: 0 },
  candidatesEndpoint: { type: String, required: true },
  extractEndpoint: { type: String, required: true },
  uploadEndpoint: { type: String, required: true },
  selectEndpoint: { type: String, required: true },
  resetEndpoint: { type: String, required: true },
  deleteEndpoint: { type: String, required: true },
  csrfToken: { type: String, required: true },
})

const playerRef = ref(null)
const candidates = ref([])
const selectedId = ref(null)
const timeHours = ref(0)
const timeMinutes = ref(0)
const timeSeconds = ref(0)
const timeMillis = ref(0)
const isExtracting = ref(false)
const isUploading = ref(false)
const timeError = ref('')
const pollingTimer = ref(null)

const totalSeconds = computed(() => {
  const h = parseInt(timeHours.value) || 0
  const m = parseInt(timeMinutes.value) || 0
  const s = parseInt(timeSeconds.value) || 0
  const ms = parseInt(timeMillis.value) || 0
  return h * 3600 + m * 60 + s + ms / 1000
})

// 翻訳ラベル（Blade から渡せないので定数）
const labels = {
  timeLabel: '時間指定',
  extractButton: 'サムネイル生成',
  extracting: 'サムネイル生成中...',
  uploadCustom: '画像をアップロード',
  uploading: 'アップロード中...',
  candidates: 'サムネイル候補',
  defaultLabel: 'デフォルト',
  uploadSource: 'アップロード',
  timeExceedError: '動画の長さを超えています',
}

const readyCandidates = computed(() => candidates.value.filter(c => c.status === 'ready'))
const pendingCandidates = computed(() => candidates.value.filter(c => c.status === 'pending'))

// 候補一覧取得
async function fetchCandidates() {
  const res = await fetch(props.candidatesEndpoint, { credentials: 'same-origin' })
  const data = await res.json()
  candidates.value = data.candidates
  selectedId.value = data.selected_thumbnail_id
}

// プレーヤーから現在時刻を取得
function syncTimeFromPlayer() {
  if (playerRef.value) {
    const t = playerRef.value.getCurrentTime()
    timeHours.value = Math.floor(t / 3600)
    timeMinutes.value = Math.floor((t % 3600) / 60)
    const remainder = t % 60
    timeSeconds.value = Math.floor(remainder)
    timeMillis.value = Math.round((remainder - Math.floor(remainder)) * 1000)
  }
}

// フレーム抽出
async function extractFrame() {
  if (isExtracting.value) return
  timeError.value = ''
  if (props.uploadDuration > 0 && totalSeconds.value > props.uploadDuration) {
    timeError.value = labels.timeExceedError
    return
  }
  isExtracting.value = true
  try {
    const res = await fetch(props.extractEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': props.csrfToken },
      credentials: 'same-origin',
      body: JSON.stringify({ timestamp: totalSeconds.value }),
    })
    if (!res.ok) throw new Error(res.status)
    const data = await res.json()
    if (data.candidate_id) {
      // pending候補として追加
      candidates.value.unshift({
        id: data.candidate_id,
        thumbnail_key: data.thumbnail_key,
        source: 'frame',
        timestamp_seconds: totalSeconds.value,
        status: 'pending',
        cdn_url: null,
      })
      startPolling()
    }
  } catch (e) {
    console.error('Extract failed:', e)
  } finally {
    isExtracting.value = false
  }
}

// カスタム画像アップロード
async function uploadCustomImage(event) {
  const file = event.target.files?.[0]
  if (!file) return
  isUploading.value = true
  try {
    const formData = new FormData()
    formData.append('file', file)
    const res = await fetch(props.uploadEndpoint, {
      method: 'POST',
      headers: { 'X-CSRF-TOKEN': props.csrfToken },
      credentials: 'same-origin',
      body: formData,
    })
    if (!res.ok) throw new Error(res.status)
    const data = await res.json()
    if (data.candidate_id) {
      candidates.value.unshift({
        id: data.candidate_id,
        thumbnail_key: data.thumbnail_key,
        source: 'upload',
        timestamp_seconds: null,
        status: 'ready',
        cdn_url: data.cdn_url,
      })
    }
  } catch (e) {
    console.error('Upload failed:', e)
  } finally {
    isUploading.value = false
    event.target.value = ''
  }
}

// 候補選択
async function selectCandidate(candidateId) {
  if (selectedId.value === candidateId) return
  try {
    const res = await fetch(props.selectEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': props.csrfToken },
      credentials: 'same-origin',
      body: JSON.stringify({ candidate_id: candidateId }),
    })
    if (!res.ok) throw new Error(res.status)
    selectedId.value = candidateId
  } catch (e) {
    console.error('Select failed:', e)
  }
}

// デフォルトに戻す
async function resetToDefault() {
  if (selectedId.value === null) return
  try {
    const res = await fetch(props.resetEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': props.csrfToken },
      credentials: 'same-origin',
    })
    if (!res.ok) throw new Error(res.status)
    selectedId.value = null
  } catch (e) {
    console.error('Reset failed:', e)
  }
}

// 候補削除
async function deleteCandidate(candidateId) {
  if (!confirm('このサムネイル候補を削除しますか？')) return
  try {
    const res = await fetch(props.deleteEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': props.csrfToken },
      credentials: 'same-origin',
      body: JSON.stringify({ candidate_id: candidateId }),
    })
    if (!res.ok) throw new Error(res.status)
    candidates.value = candidates.value.filter(c => c.id !== candidateId)
    if (selectedId.value === candidateId) {
      selectedId.value = null
    }
  } catch (e) {
    console.error('Delete failed:', e)
  }
}

// ポーリング（pending候補の完了チェック）
function startPolling() {
  if (pollingTimer.value) return
  pollingTimer.value = setInterval(async () => {
    await fetchCandidates()
    if (pendingCandidates.value.length === 0) {
      clearInterval(pollingTimer.value)
      pollingTimer.value = null
    }
  }, 3000)
}

// タイムスタンプフォーマット
function formatTimestamp(seconds) {
  if (seconds == null) return ''
  const s = parseFloat(seconds)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = Math.floor(s % 60)
  const ms = Math.round((s % 1) * 1000)
  if (ms > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}:${String(ms).padStart(3, '0')}`
  }
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

onMounted(() => {
  fetchCandidates()
})

onBeforeUnmount(() => {
  if (pollingTimer.value) {
    clearInterval(pollingTimer.value)
  }
})
</script>
